import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { isGuid, store } from "commonui";
import {
	LanguageSkillModel,
	PersistedData,
	PersistedDataPayload,
	TagModel,
	VCSLevelModel
} from "@/models/title-list-model";
import { TitleListService } from "@/services";
import {
	ComponentContentStateModel,
	ContentForEditModel,
	ContentService,
	CurrentContentDetailsForm,
	PossibleChapterModel
} from "@/services/content";
import { VersionGroupModule } from "./versiongroup";
import { orderBy } from "lodash";
import { ContentCategoryEnum, CurriculumType, NonCurriculumComponentType, ParentType } from "@/util/enum";
import { ComponentService } from "@/services/component-service";

const defaultPersistedData = {
	searchTerm: "",
	currentPage: 1,
	currentSort: {
		column: "",
		ascending: false
	}
};
@Module({ namespaced: true, name: "TitleListState", store: store, dynamic: true })
export default class TitleListState extends VuexModule {
	// State for title tab
	languageSkills: LanguageSkillModel[] = [];
	levels: VCSLevelModel[] = [];
	tags: TagModel[] = [];
	isunitBased: NonCurriculumComponentType = NonCurriculumComponentType.Unit;
	editedContentDetails: ContentForEditModel = {} as ContentForEditModel; // content which is being edited details.
	updatedDetails: CurrentContentDetailsForm = {} as CurrentContentDetailsForm; // this will have latest values from the form
	editeContentSelectedChapterId: string = "none"; // used to store selected chapterid of common form when a content have chapters
	possibleEditeContentSelectedChapterId: string = "";
	editedContentId: string = "";
	loading: boolean = false;
	disableForm: boolean = false;

	isParent: boolean = false; // denotes Weather title is a parent
	checkIsParent: boolean = false;

	isInfo: boolean = false; // denotes if the content is meta content

	validForm: boolean = false;
	formTouched: boolean = false;
	validText: boolean = false;
	textPageDirty: boolean = false;
	savingTitle: boolean = false;
	componentContentState: ComponentContentStateModel = {
		hasText: false,
		hasREPPlan: false,
		hasConversation: false,
		mediaSlots: []
	};
	hasTextDetails: boolean = false;
	isFetchingComponentContentState: boolean = false;

	listSelectedLevel: number = 0;
	listSelectedSkills: number[] = [-1];

	currentRevision: number = -1;
	revisionChanged: boolean = false; // denotes if revision is changed by used in comparer screen
	forceProcess: boolean = false;
	isREP: boolean = false;
	isREPCM: boolean = false;
	allowTeachingPlan: boolean = false;
	allowRepPlan: boolean = false;
	isSubCategorySupportResource: boolean = false;

	// Chapter linking state variables
	possibleChapters: PossibleChapterModel[] = [];
	loadingPossibleChapters = false;

	updateTitleInfo = false; // when user clicks on save on manage chapter screen.
	// hideREP4ContentTypeIds: number[] = [301];
	persistedData: PersistedData = defaultPersistedData;
	@Mutation
	setUpdatedDetails(value: CurrentContentDetailsForm) {
		this.updatedDetails = value;
	}

	@Mutation
	setUpdateTitleInfo(value: boolean) {
		this.updateTitleInfo = value;
	}

	@Mutation
	setREPCM({ category, contentTypeId }: { category: ContentCategoryEnum; contentTypeId?: number }) {
		const repcm = [ContentCategoryEnum.REP, ContentCategoryEnum.CM];
		this.isREP = contentTypeId
			? category == repcm[0] //&& !this.hideREP4ContentTypeIds.some((ctIds) => ctIds == contentTypeId)
			: category == repcm[0];
		this.isREPCM = repcm.includes(category);
	}

	@Mutation
	setAllowTeachingPlan(value: boolean) {
		this.allowTeachingPlan = value;
	}

	@Mutation
	setAllowRepPlan(value: boolean) {
		this.allowRepPlan = value;
	}

	@Mutation
	setIsSubCategorySupportResource(value: boolean) {
		this.isSubCategorySupportResource = value;
	}

	@Mutation
	setEditeContentSelectedChapterId(value: string) {
		this.editeContentSelectedChapterId = value;
	}

	@Mutation
	setPossibleEditeContentSelectedChapterId(value: string) {
		this.possibleEditeContentSelectedChapterId = value;
	}

	@Mutation
	setIsParent(value: boolean) {
		this.isParent = value;
		if (value) {
			this.editeContentSelectedChapterId = "all";
		} else {
			this.editeContentSelectedChapterId = "none";
		}
	}

	@Mutation
	setisInfo(value: boolean) {
		this.isInfo = value;
	}

	@Mutation
	setOnlyIsParent(value: boolean) {
		this.isParent = value;
	}

	@Mutation
	setUnitBased(value: NonCurriculumComponentType) {
		this.isunitBased = value;
	}

	@Mutation
	setCheckboxParent(value: boolean) {
		this.checkIsParent = value;
	}

	@Mutation
	setForceProcess(value: boolean) {
		this.forceProcess = value;
	}

	@Mutation
	setRevisionChanged(value: boolean) {
		this.revisionChanged = value;
	}

	@Mutation
	setRevision(value: number) {
		this.currentRevision = value;
	}

	@Mutation
	setListSelectedLevel(value: number) {
		this.listSelectedLevel = value;
	}

	@Mutation
	setListSelectedSkills(value: number[]) {
		this.listSelectedSkills = value;
	}

	@Mutation
	setSavingTitle(value: boolean) {
		this.savingTitle = value;
	}

	@Mutation
	setEditedContentId(id: string) {
		this.editedContentId = id;
	}

	@Mutation
	setLanguageSkills(skills: LanguageSkillModel[]) {
		this.languageSkills = skills;
	}

	@Mutation
	setLevels(level: VCSLevelModel[]) {
		const orderedLevel = orderBy(level, ["curriculumType", "unit"], ["desc", "asc"]);
		this.levels = orderedLevel;
	}

	@Action
	async fetchLanguageSkills() {
		const titleList = await TitleListService.getLanguageSkills();
		this.setLanguageSkills(titleList);
	}

	@Action
	async fetchVcsLevels() {
		//getting all units including disabled
		const levels = await TitleListService.getLevels([], true);
		this.setLevels(levels);
	}

	@Action
	async fetchVcsTags() {
		const tags = await TitleListService.getTags();
		this.setTags(tags);
	}

	@Action
	async fetchContentDetails(contentId: string | undefined = undefined) {
		const getDetails = async () => {
			const gsVersion = isGuid(VersionGroupModule.selectedGsVersion)
				? VersionGroupModule.selectedGsVersion
				: undefined;
			const lsVersion = isGuid(VersionGroupModule.selectedLsVersion)
				? VersionGroupModule.selectedLsVersion
				: undefined;
			contentId = contentId ? contentId : this.editedContentId.length ? this.editedContentId : "";
			if (!contentId || !contentId.length) {
				this.setLoading(false);
				return;
			}
			// If version is none go to title list page
			if (
				(this.editedContentDetails.curriculumType === CurriculumType.GrapeSEED &&
					!isGuid(VersionGroupModule.selectedGsVersion)) ||
				(this.editedContentDetails.curriculumType === CurriculumType.LittleSEED &&
					!isGuid(VersionGroupModule.selectedLsVersion))
			) {
				this.setLoading(false);
				this.setContentDetails({ parentDontContainsContent: true } as any);
				return;
			}
			// fetch details
			try {
				const details = await ContentService.getContentWithText(
					contentId,
					this.currentRevision,
					gsVersion,
					lsVersion
				);

				if (this.currentRevision === -1 && this.editeContentSelectedChapterId === "none") {
					// set initial chapter and revision
					if (details.parentTypeId === ParentType.Parent || details.chapters.length) {
						this.setIsParent(true);
						// Revision case for this is not needed because when chapters are there then default chapter would be all
					} else {
						// if simple title set the revision
						this.setRevision(details.revisions);
					}
				}
				this.setValidText(false);
				this.setValidForm(false);
				this.setFormTocuched(false);
				this.textPageDirty && this.togglePageDirty();
				this.changeChapterContentInfo(
					details.chapters.find((ch) => ch.id === this.editeContentSelectedChapterId)?.isInfo || false
				);
				this.setContentDetails(details);
				this.setEditedContentId(contentId);
			} catch (e) {
				// if any error occours. Redirect to title list (components) page.
				this.setLoading(false);
				this.setContentDetails({ parentDontContainsContent: true } as any);
			}
		};
		while (VersionGroupModule.loading) {
			await new Promise<void>((res) => setTimeout(() => res(), 400));
		}
		this.setLoading(true);
		await getDetails();
	}

	@Action
	async getComponentContentState(contentId: string) {
		if (this.isFetchingComponentContentState) {
			return;
		}
		this.setIsFetchingComponentContentState(true);
		const gsVersion = isGuid(VersionGroupModule.selectedGsVersion)
			? VersionGroupModule.selectedGsVersion
			: undefined;
		const lsVersion = isGuid(VersionGroupModule.selectedLsVersion)
			? VersionGroupModule.selectedLsVersion
			: undefined;
		const contentState = await ComponentService.getComponentContentState(
			contentId,
			this.currentRevision,
			gsVersion,
			lsVersion
		);
		this.setComponentContentState(contentState);
		this.setIsFetchingComponentContentState(false);
	}

	//fetch content which can be chapters.
	@Action
	async updatePossibleChildComponents(levelId: number) {
		this.setPossibleChaptersLoading(true);
		const curriculumType = this.levels.find((l) => l.id == levelId)?.curriculumType as number;
		const versionId =
			curriculumType === CurriculumType.GrapeSEED
				? VersionGroupModule.selectedGsVersion
				: VersionGroupModule.selectedLsVersion;

		try {
			let childs = await ContentService.getPossibleChildren(levelId, versionId);
			// If in edit mode, remove the current title from possible children
			if (this.editedContentDetails.id) {
				childs = childs.filter((f) => f.id !== this.editedContentDetails.id);
			}
			this.setPossibleChapters(childs);
			this.setPossibleChaptersLoading(false);
		} catch (e) {
			this.setPossibleChaptersLoading(false);
		}
	}

	@Mutation
	setPossibleChaptersLoading(value: boolean) {
		this.loadingPossibleChapters = value;
	}

	@Mutation
	setPossibleChapters(value: PossibleChapterModel[]) {
		this.possibleChapters = value;
	}

	@Mutation
	setLoading(loading: boolean) {
		this.loading = loading;
	}

	@Mutation
	setTags(tags: TagModel[]) {
		this.tags = tags;
	}

	@Mutation
	setContentDetails(details: ContentForEditModel) {
		this.editedContentDetails = details;
		this.loading = false;
		this.validForm = true;
		this.validText = true;
	}

	@Mutation
	resetContentDetails() {
		this.editedContentDetails = {} as ContentForEditModel;
	}

	@Mutation
	togglePageDirty() {
		this.textPageDirty = !this.textPageDirty;
	}

	@Mutation
	setValidForm(isValid: boolean) {
		this.validForm = isValid;
	}

	@Mutation
	setFormTocuched(touched: boolean) {
		this.formTouched = touched;
	}

	@Mutation
	setValidText(isValid: boolean) {
		this.validText = isValid;
	}

	@Mutation
	resetChildState() {
		this.validForm = false;
		this.validText = false;
		this.textPageDirty = false;
		this.currentRevision = -1;
		this.editedContentId = "";
		this.editedContentDetails = {} as ContentForEditModel;
		this.editeContentSelectedChapterId = "none";
		this.isParent = false;
		this.formTouched = false;
		this.checkIsParent = false;
	}

	@Mutation
	setTextState() {
		this.validForm = true;
		this.validText = true;
		this.textPageDirty = false;
	}

	@Mutation
	setDisableForm(value: boolean) {
		this.disableForm = value;
	}

	@Mutation
	setHasTextDetails(value: boolean) {
		this.hasTextDetails = value;
	}

	@Mutation
	setComponentContentState(value: ComponentContentStateModel) {
		this.componentContentState = value;
	}

	@Mutation
	setIsFetchingComponentContentState(value: boolean) {
		this.isFetchingComponentContentState = value;
	}

	get getEditedContentDetails() {
		return this.editedContentDetails;
	}

	@Mutation
	setPersistedData(payload?: PersistedData) {
		// if payload is undefined, reset the persistedData
		this.persistedData = payload ? payload : defaultPersistedData;
	}

	@Action
	changePersistedData(payload?: PersistedData) {
		this.setPersistedData(payload);
	}

	@Action
	changeChapterContentInfo(payload: boolean = false) {
		this.setisInfo(payload);
	}
}
